/**
 * Turning AI job results into the shapes the Add Content form already saves.
 *
 * These helpers deliberately contain NO React and NO form state — they take a job
 * result plus the current list, and return the next list. That makes the merge
 * rules (below) unit-testable and keeps them in one place instead of being
 * re-derived at each call site.
 *
 * ── The merge rule: an explicit generate REPLACES ─────────────────────────────
 * The per-section AI panels currently SKIP any language that already exists. That
 * silently throws away a freshly generated file: pressing "Generate" after a fix
 * appears to do nothing, because the correct new subtitle is discarded and the old
 * one kept. When an operator explicitly asks to generate a language, the new
 * output is the one they want, so `mergeSubtitleFiles` / `mergeAudioTracks`
 * replace in place.
 *
 * Replacing a row that came from the server means dropping it from the list, which
 * drops its id from the save payload's `*_keep_ids` — the backend then deletes the
 * old row and stores the newly uploaded file in its place. That is exactly the
 * intended "regenerate this language" behaviour.
 */

/** Lowercased language code, or "" when absent. */
const langKey = (value) => String(value || "").toLowerCase();

/** True for a row loaded from the server (numeric DB id) rather than a local pick. */
const isServerAudioRow = (track) =>
  track?.id != null && /^\d+$/.test(String(track.id));

/**
 * Fetch each generated subtitle URL into a real `File`, tagged with its language
 * and format. Returning genuine File objects is what lets AI subtitles travel the
 * exact same save path as a manually picked .srt — no special-casing downstream.
 *
 * Individual failures are collected rather than thrown: one unreachable file must
 * not discard the languages that did download.
 *
 * @param {{subtitles?: Array<{language:string, format:string, url:string, translated?:boolean}>}} result
 * @returns {Promise<{files: File[], failed: string[]}>}
 */
export const subtitleResultToFiles = async (result) => {
  const generated = result?.subtitles || [];
  const files = [];
  const failed = [];

  await Promise.all(
    generated.map(async (sub) => {
      const language = langKey(sub.language);
      if (!language || !sub.url) return;
      try {
        const res = await fetch(sub.url);
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        const text = await res.text();
        const format = sub.format || "srt";
        const file = new File([text], `${language}.${format}`, {
          type: format === "vtt" ? "text/vtt" : "application/x-subrip",
        });
        // Read by the save path (Mp4FileUpload prefers file.language/format over
        // its positional fallback) and by the subtitle card badges.
        file.language = language;
        file.format = format;
        file.aiGenerated = true;
        file.translated = Boolean(sub.translated);
        files.push(file);
      } catch (err) {
        failed.push(sub.language || "unknown");
        console.warn(`[ai-studio] subtitle ${sub.language} could not be loaded: ${err.message}`);
      }
    })
  );

  return { files, failed };
};

/**
 * Merge freshly generated subtitle Files into the form's subtitle list, replacing
 * any entry that already holds the same language (see the module header).
 *
 * The list is the combined shape the container saves: `null` holes, `{isExisting}`
 * server rows, and `File` picks. Holes are reused before the list is extended, so
 * the existing fixed-slot layout keeps working unchanged.
 *
 * @param {Array<null|File|object>} current
 * @param {File[]} incoming  Files carrying `.language`
 * @returns {{next: Array, replaced: string[], added: string[]}}
 */
export const mergeSubtitleFiles = (current = [], incoming = []) => {
  const next = [...(current || [])];
  const replaced = [];
  const added = [];

  incoming.forEach((file) => {
    const language = langKey(file.language);

    const existingIndex = next.findIndex(
      (entry) => entry && langKey(entry.language) === language
    );

    if (existingIndex !== -1) {
      next[existingIndex] = file;
      replaced.push(language);
      return;
    }

    const hole = next.findIndex((entry) => !entry);
    if (hole !== -1) next[hole] = file;
    else next.push(file);
    added.push(language);
  });

  return { next, replaced, added };
};

/**
 * Split server-loaded subtitle rows into the ones to keep and the ones a freshly
 * generated language supersedes.
 *
 * Needed because the subtitle UI holds server rows and local file picks in two
 * separate lists. Replacing a language means removing its server row here as well as
 * merging the new file in — the removal is what drops that row's id from the save
 * payload's `subtitles_keep_ids`, so the backend deletes the stale subtitle rather
 * than keeping both.
 *
 * @param {Array<{language?:string, id?:number}>} prefill
 * @param {string[]} languages  language codes that were just generated
 * @returns {{kept: object[], removed: object[]}}
 */
export const dropSubtitleLanguages = (prefill = [], languages = []) => {
  const drop = new Set((languages || []).map(langKey).filter(Boolean));
  const kept = [];
  const removed = [];
  (prefill || []).forEach((row) => {
    if (row && drop.has(langKey(row.language))) removed.push(row);
    else kept.push(row);
  });
  return { kept, removed };
};

/**
 * Fetch each generated dub URL into a File and build the audio-track rows the form
 * saves. The blob URL is kept only for the in-CMS `<audio>` preview; the File is
 * what gets uploaded on Save.
 *
 * @param {{audioTracks?: Array<{language:string, url:string, format?:string, voice?:string}>}} result
 * @param {(code:string)=>string} nameFor  language code → display name
 * @returns {Promise<{tracks: object[], failed: string[]}>}
 */
export const dubResultToTracks = async (result, nameFor = (c) => c) => {
  const generated = result?.audioTracks || [];
  const tracks = [];
  const failed = [];

  await Promise.all(
    generated.map(async (t) => {
      const language = langKey(t.language);
      if (!language || !t.url) return;
      try {
        const res = await fetch(t.url);
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        const blob = await res.blob();
        const format = t.format || "m4a";
        const file = new File([blob], `dub_${language}.${format}`, { type: "audio/mp4" });
        file.language = language;
        file.format = format;
        file.aiGenerated = true;
        file.dubbed = true;

        tracks.push({
          id: `dub-${language}`,
          language: nameFor(language),
          languageCode: language,
          codec: "AAC",
          channels: "2.0",
          // Name the track after its language so the player's audio menu reads
          // "English (AI Dub)" rather than a generic "AI Dub" for every entry.
          label: `${nameFor(language)} (AI Dub)`,
          format,
          previewUrl: URL.createObjectURL(blob),
          file,
          dubbed: true,
          voice: t.voice,
        });
      } catch (err) {
        failed.push(t.language || "unknown");
        console.warn(`[ai-studio] dub ${t.language} could not be loaded: ${err.message}`);
      }
    })
  );

  return { tracks, failed };
};

/**
 * Merge generated dub tracks into the audio-track list, replacing any track that
 * already covers the same language.
 *
 * A replaced local row's object-URL preview is revoked so repeated regeneration
 * doesn't leak blobs.
 *
 * @param {object[]} current
 * @param {object[]} incoming
 * @returns {{next: object[], replaced: string[], added: string[]}}
 */
export const mergeAudioTracks = (current = [], incoming = []) => {
  const next = [...(current || [])];
  const replaced = [];
  const added = [];

  incoming.forEach((track) => {
    const language = langKey(track.languageCode || track.language);

    const existingIndex = next.findIndex((row) => {
      if (!row) return false;
      const rowLang = langKey(row.languageCode || row.language);
      return rowLang === language;
    });

    if (existingIndex !== -1) {
      const previous = next[existingIndex];
      // Only local previews own a blob URL; server rows carry a real CDN url.
      if (previous?.previewUrl && !isServerAudioRow(previous)) {
        try {
          URL.revokeObjectURL(previous.previewUrl);
        } catch {
          /* a non-blob url — nothing to revoke */
        }
      }
      next[existingIndex] = track;
      replaced.push(language);
      return;
    }

    next.push(track);
    added.push(language);
  });

  return { next, replaced, added };
};

/**
 * Normalize a trailer result into the trailer row the form renders.
 *
 * Two different job shapes arrive here and both must work:
 *  - the METADATA job nests it as `result.trailer.value` with a `description`
 *  - the standalone TRAILER job (`generateTrailer`) returns the trailer's fields
 *    FLATTENED at the top level, and calls the blurb `tagline` rather than
 *    `description`
 * Hence the unwrap chain and the description/tagline fallback — without the
 * latter, a trailer generated by the dedicated job silently loses its blurb.
 *
 * `videoFile` stays null: the operator attaches the actual file, AI fills the
 * descriptive fields around it.
 */
export const trailerResultToRow = (result) => {
  const t = result?.trailer?.value || result?.trailer || result;
  if (!t || (!t.url && !t.title)) return null;
  return {
    id: Date.now(),
    label: t.title || "",
    type: t.type || "",
    description: t.description || t.tagline || "",
    url: t.url || "",
    duration: t.duration ?? null,
    videoFile: null,
    thumbnail: t.thumbnail || null,
    subtitlesEnabled: false,
    subtitleFiles: [null, null, null],
    expanded: true,
    saved: false,
  };
};

/** A short "Replaced X · Added Y" line for the completion toast. */
export const describeMerge = ({ replaced = [], added = [] }, nameFor = (c) => c) => {
  const parts = [];
  if (added.length) parts.push(`added ${added.map(nameFor).join(", ")}`);
  if (replaced.length) parts.push(`replaced ${replaced.map(nameFor).join(", ")}`);
  return parts.join(" · ");
};
